/**
 * PartnerForm - Form component for creating/editing partners with provider profile
 */

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Box,
  Grid,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
  MenuItem,
} from "@mui/material";
import {
  createPartner,
  updatePartner,
  getPartner,
} from "../services/partnerService";
import ProviderOptionMultiSelect from "./ProviderOptionMultiSelect.jsx";
import { getActiveRouteCorridors } from "../../routeCorridors/services/routeCorridorService.js";
import {
  SA_PROVINCE_OPTIONS,
  PROVIDER_SERVICE_OPTIONS,
  PROVIDER_VEHICLE_TYPE_OPTIONS,
  BUSINESS_TYPE_OPTIONS,
  SETTLEMENT_PREFERENCE_OPTIONS,
  DEFAULT_ROUTE_CORRIDOR_OPTIONS,
  MAJOR_CITY_OPTIONS,
} from "../constants/providerProfileOptions.js";
import {
  createEmptyBasicFields,
  createEmptyProviderProfile,
  createEmptyBankFields,
  basicFieldsFromPartner,
  profileFromPartner,
  bankFromPartner,
  buildProviderProfilePayload,
  buildContractDetailsWithBank,
} from "../utils/providerProfileForm.js";

const PartnerForm = ({ open, partner, onClose, onSave }) => {
  const [tab, setTab] = useState(0);
  const [routeOptions, setRouteOptions] = useState(DEFAULT_ROUTE_CORRIDOR_OPTIONS);
  const [formData, setFormData] = useState(createEmptyBasicFields);
  const [providerProfile, setProviderProfile] = useState(createEmptyProviderProfile);
  const [bankFields, setBankFields] = useState(createEmptyBankFields);
  const [contractDetailsBase, setContractDetailsBase] = useState({});
  const [loading, setLoading] = useState(false);
  const [hydrating, setHydrating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    let cancelled = false;
    setTab(0);
    setError(null);

    if (!partner) {
      setFormData(createEmptyBasicFields());
      setProviderProfile(createEmptyProviderProfile());
      setBankFields(createEmptyBankFields());
      setContractDetailsBase({});
      setHydrating(false);
      return undefined;
    }

    // Seed immediately from the list row, then refresh from API so
    // providerProfile (services/coverage/fleet/commercial) is authoritative.
    setFormData(basicFieldsFromPartner(partner));
    setProviderProfile(profileFromPartner(partner));
    setBankFields(bankFromPartner(partner));
    setContractDetailsBase(partner.contractDetails || {});
    setHydrating(true);

    (async () => {
      const result = await getPartner(partner.id);
      if (cancelled) {
        return;
      }

      if (result?.success && result.data) {
        setFormData(basicFieldsFromPartner(result.data));
        setProviderProfile(profileFromPartner(result.data));
        setBankFields(bankFromPartner(result.data));
        setContractDetailsBase(result.data.contractDetails || {});
      } else if (result && result.success === false) {
        setError(result.error || "Failed to load partner details");
      }
      setHydrating(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [partner, open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const corridors = await getActiveRouteCorridors();
        if (cancelled || !Array.isArray(corridors) || corridors.length === 0) {
          return;
        }
        setRouteOptions(
          corridors.map((corridor) => ({
            value: corridor.name,
            label: corridor.description
              ? `${corridor.name} — ${corridor.description}`
              : corridor.name,
          }))
        );
      } catch {
        setRouteOptions(DEFAULT_ROUTE_CORRIDOR_OPTIONS);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleProfileChange = (section, field, value) => {
    setProviderProfile((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
    setError(null);
  };

  const handleBankChange = (field, value) => {
    setBankFields((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError("Name is required");
      return false;
    }
    if (!formData.contactPerson.trim()) {
      setError("Contact person is required");
      return false;
    }
    if (!formData.phone.trim()) {
      setError("Phone is required");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const bankReady = Boolean(
        bankFields.bankName?.trim() &&
          bankFields.accountHolder?.trim() &&
          bankFields.accountNumber?.trim() &&
          bankFields.branchCode?.trim()
      );
      const profilePayload = buildProviderProfilePayload(providerProfile);
      profilePayload.commercialSummary = {
        ...profilePayload.commercialSummary,
        bankReady,
      };
      const payload = {
        ...formData,
        providerProfile: profilePayload,
        // CONFIDENTIAL / PII financial — do not log accountNumber
        contractDetails: buildContractDetailsWithBank(
          contractDetailsBase,
          bankFields
        ),
      };
      const result = partner
        ? await updatePartner(partner.id, payload)
        : await createPartner(payload);

      if (result.success) {
        onSave();
      } else {
        setError(result.error || "Failed to save partner");
      }
    } catch (err) {
      setError(err.message || "Failed to save partner");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{partner ? "Edit Partner" : "Create Partner"}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {hydrating && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Loading saved partner profile…
          </Alert>
        )}

        <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 2 }}>
          <Tab label="Basic" />
          <Tab label="Services & Coverage" />
          <Tab label="Fleet & Compliance" />
          <Tab label="Commercial" />
        </Tabs>

        <Box sx={{ pt: 1 }}>
          {tab === 0 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contact Person"
                  value={formData.contactPerson}
                  onChange={(e) => handleChange("contactPerson", e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={formData.phone}
                  onChange={(e) => handleChange("phone", e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.phoneIsWhatsApp}
                      onChange={(e) =>
                        handleChange("phoneIsWhatsApp", e.target.checked)
                      }
                    />
                  }
                  label="This phone number is WhatsApp-ready"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  multiline
                  rows={2}
                  value={formData.address}
                  onChange={(e) => handleChange("address", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Trading name"
                  value={providerProfile.businessProfile.tradingName}
                  onChange={(e) =>
                    handleProfileChange(
                      "businessProfile",
                      "tradingName",
                      e.target.value
                    )
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Business type"
                  value={providerProfile.businessProfile.businessType}
                  onChange={(e) =>
                    handleProfileChange(
                      "businessProfile",
                      "businessType",
                      e.target.value
                    )
                  }
                >
                  <MenuItem value="">
                    <em>Select business type</em>
                  </MenuItem>
                  {BUSINESS_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          )}

          {tab === 1 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <ProviderOptionMultiSelect
                  label="Services"
                  options={PROVIDER_SERVICE_OPTIONS}
                  value={providerProfile.serviceProfile.services}
                  onChange={(values) =>
                    handleProfileChange("serviceProfile", "services", values)
                  }
                  helperText="Select all services this provider offers"
                />
              </Grid>
              <Grid item xs={12}>
                <ProviderOptionMultiSelect
                  label="Coverage routes"
                  options={routeOptions}
                  value={providerProfile.coverage.routes}
                  onChange={(values) =>
                    handleProfileChange("coverage", "routes", values)
                  }
                  helperText="Route corridor codes used for matching (e.g. GP-WC)"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ProviderOptionMultiSelect
                  label="Provinces"
                  options={SA_PROVINCE_OPTIONS}
                  value={providerProfile.coverage.provinces}
                  onChange={(values) =>
                    handleProfileChange("coverage", "provinces", values)
                  }
                  helperText="Province codes (GP, WC, KZN, …)"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ProviderOptionMultiSelect
                  label="Cities"
                  options={MAJOR_CITY_OPTIONS}
                  value={providerProfile.coverage.cities}
                  onChange={(values) =>
                    handleProfileChange("coverage", "cities", values)
                  }
                  helperText="Major cities this provider serves"
                />
              </Grid>
              <Grid item xs={12}>
                <ProviderOptionMultiSelect
                  label="Vehicle types"
                  options={PROVIDER_VEHICLE_TYPE_OPTIONS}
                  value={providerProfile.serviceProfile.vehicleTypes}
                  onChange={(values) =>
                    handleProfileChange("serviceProfile", "vehicleTypes", values)
                  }
                  helperText="Fleet types available for jobs"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Service notes"
                  value={providerProfile.serviceProfile.notes}
                  onChange={(e) =>
                    handleProfileChange("serviceProfile", "notes", e.target.value)
                  }
                />
              </Grid>
            </Grid>
          )}

          {tab === 2 && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Vehicle count"
                  type="number"
                  value={providerProfile.fleetSummary.vehicleCount}
                  onChange={(e) =>
                    handleProfileChange("fleetSummary", "vehicleCount", e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={providerProfile.fleetSummary.hasTailLift}
                      onChange={(e) =>
                        handleProfileChange(
                          "fleetSummary",
                          "hasTailLift",
                          e.target.checked
                        )
                      }
                    />
                  }
                  label="Tail lift available"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={providerProfile.fleetSummary.hasBlankets}
                      onChange={(e) =>
                        handleProfileChange(
                          "fleetSummary",
                          "hasBlankets",
                          e.target.checked
                        )
                      }
                    />
                  }
                  label="Blankets/strapping"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={providerProfile.fleetSummary.hasGps}
                      onChange={(e) =>
                        handleProfileChange(
                          "fleetSummary",
                          "hasGps",
                          e.target.checked
                        )
                      }
                    />
                  }
                  label="GPS tracking"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Compliance status"
                  value={providerProfile.complianceSummary.status}
                  onChange={(e) =>
                    handleProfileChange(
                      "complianceSummary",
                      "status",
                      e.target.value
                    )
                  }
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="verified">Verified</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                  <MenuItem value="suspended">Suspended</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          )}

          {tab === 3 && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Payment terms"
                  value={providerProfile.commercialSummary.paymentTerms}
                  onChange={(e) =>
                    handleProfileChange(
                      "commercialSummary",
                      "paymentTerms",
                      e.target.value
                    )
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Settlement preference"
                  value={providerProfile.commercialSummary.settlementPreference}
                  onChange={(e) =>
                    handleProfileChange(
                      "commercialSummary",
                      "settlementPreference",
                      e.target.value
                    )
                  }
                >
                  <MenuItem value="">
                    <em>Select preference</em>
                  </MenuItem>
                  {SETTLEMENT_PREFERENCE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Typical pricing notes"
                  value={providerProfile.commercialSummary.typicalPricingNotes}
                  onChange={(e) =>
                    handleProfileChange(
                      "commercialSummary",
                      "typicalPricingNotes",
                      e.target.value
                    )
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Box
                  component="fieldset"
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    m: 0,
                    p: 2,
                  }}
                >
                  <Box
                    component="legend"
                    sx={{ px: 0.5, typography: "subtitle2", color: "text.secondary" }}
                  >
                    Bank details (payout offers)
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Bank name"
                        value={bankFields.bankName}
                        onChange={(e) =>
                          handleBankChange("bankName", e.target.value)
                        }
                        helperText="e.g. FNB, Standard Bank, Absa"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Account holder"
                        value={bankFields.accountHolder}
                        onChange={(e) =>
                          handleBankChange("accountHolder", e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Account number"
                        value={bankFields.accountNumber}
                        onChange={(e) =>
                          handleBankChange("accountNumber", e.target.value)
                        }
                        autoComplete="off"
                        inputProps={{ inputMode: "numeric" }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Branch code"
                        value={bankFields.branchCode}
                        onChange={(e) =>
                          handleBankChange("branchCode", e.target.value)
                        }
                        helperText="SA branch / universal code"
                        inputProps={{ inputMode: "numeric" }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading || hydrating}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || hydrating}
        >
          {loading ? "Saving..." : partner ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PartnerForm;
